/**
 * Rules Restore Command
 *
 * Restore previously backed up rules
 */

import { Command } from 'commander';
import { promises as fs } from 'fs';
import { join } from 'path';
import { select, confirm } from '@inquirer/prompts';
import { BackupService } from '../../services/backup.service.js';
import { ValidationError } from '../../errors/index.js';
import { selectTheme } from '../../utils/prompt-theme.js';

export function createRulesRestoreCommand(): Command {
    return new Command('restore')
        .description('Restore rules from a backup')
        .option('--latest', 'Restore the most recent backup')
        .option('-y, --yes', 'Skip confirmation')
        .action(async (options) => {
            const backupService = new BackupService();

            // List available backups
            const backups = await backupService.listBackups();

            if (backups.length === 0) {
                console.log('No backups available.');
                return;
            }

            let selectedBackup;

            if (options.latest) {
                selectedBackup = backups[0];
                console.log(`📦 Selected latest backup: ${selectedBackup.timestamp}`);
            } else {
                // Show interactive selection
                console.log('📋 Available backups:\n');

                const backupTimestamp = await select({
                    message: 'Select a backup to restore:',
                    choices: backups.map((backup) => ({
                        name: formatBackupInfo(backup),
                        value: backup.timestamp,
                        description: `${backup.files.length} files | IDEs: ${backup.ides.join(', ')}`,
                    })),
                    theme: selectTheme,
                });

                selectedBackup = backups.find((b) => b.timestamp === backupTimestamp);

                if (!selectedBackup) {
                    throw new ValidationError('Backup not found');
                }
            }

            // Show backup details
            console.log('\n📊 Backup Details:');
            console.log(`  Timestamp: ${formatTimestamp(selectedBackup.timestamp)}`);
            console.log(`  Preset: ${selectedBackup.preset || 'N/A'}`);
            console.log(`  IDEs: ${selectedBackup.ides.join(', ')}`);
            console.log(`  Files: ${selectedBackup.files.length}`);

            // Confirm restoration
            if (!options.yes) {
                const proceed = await confirm({
                    message: 'This will overwrite current rules. Continue?',
                    default: false,
                });

                if (!proceed) {
                    console.log('Cancelled.');
                    return;
                }
            }

            // Restore backup
            console.log('\n🔄 Restoring backup...\n');

            try {
                const backupPath = join(backupService.getBackupDir(), selectedBackup.timestamp);
                await backupService.restoreBackup(backupPath);

                console.log('\n✅ Backup restored successfully!\n');
                console.log('💡 Tip: Your IDE may need to be restarted to pick up the changes.');
            } catch (error) {
                console.error('\n❌ Failed to restore backup:', error);
                throw error;
            }
        });
}

/**
 * Format backup info for display
 */
function formatBackupInfo(backup: any): string {
    const timestamp = formatTimestamp(backup.timestamp);
    const preset = backup.preset ? ` [${backup.preset}]` : '';
    return `${timestamp}${preset}`;
}

/**
 * Format timestamp for display
 */
function formatTimestamp(timestamp: string): string {
    try {
        const date = new Date(timestamp);
        return date.toLocaleString();
    } catch {
        return timestamp;
    }
}
